/** localStorage key — deploy id from /version.json (production builds only). */
export const DEPLOY_ID_STORAGE_KEY = 'nidata_deploy_id';

export function parseDeployId(data: unknown): string | null {
  if (data && typeof data === 'object' && 'v' in data) {
    const v = (data as { v: unknown }).v;
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return null;
}

export type DeployVersionOutcome = 'reload' | 'first-set' | 'unchanged';

/** Pure decision helper for tests and ensureFreshDeploy. */
export function outcomeForDeployCheck(prev: string | null, current: string): DeployVersionOutcome {
  if (prev !== null && prev !== current) return 'reload';
  if (prev === null) return 'first-set';
  return 'unchanged';
}

/**
 * After a new deploy, the browser may still run an old JS bundle until the tab loads fresh HTML.
 * Fetch a tiny version file (no-store), compare to last visit, and reload once so users get the new app.
 * No UI — skipped entirely in Vite dev.
 */
export async function ensureFreshDeploy(): Promise<void> {
  if (import.meta.env.DEV) return;

  let res: Response;
  try {
    res = await fetch('/version.json', { cache: 'no-store' });
  } catch {
    return;
  }
  if (!res.ok) return;

  let data: unknown;
  try {
    data = await res.json();
  } catch {
    return;
  }

  const current = parseDeployId(data);
  if (!current) return;

  const prev = localStorage.getItem(DEPLOY_ID_STORAGE_KEY);
  const outcome = outcomeForDeployCheck(prev, current);

  if (outcome === 'reload') {
    localStorage.setItem(DEPLOY_ID_STORAGE_KEY, current);
    window.location.reload();
    return;
  }
  if (outcome === 'first-set') {
    localStorage.setItem(DEPLOY_ID_STORAGE_KEY, current);
  }
}
